import { addMinutes } from "date-fns/addMinutes";

export interface EventDayLayout {
  widthPercent: number;
  leftPercent: number;
}

const MIN_DISPLAY_DURATION_MINUTES = 15;
const OVERLAP_PERCENT = 10;

interface OverlappingEventInput {
  id: string;
  startTimeInMS: number;
  durationInMinutes: number;
  actualDuration?: number | null;
}

interface PreparedEvent {
  id: string;
  start: number;
  displayEnd: number;
}

interface PlacedEvent extends PreparedEvent {
  column: number;
}

const compareEvents = (a: PreparedEvent, b: PreparedEvent) => {
  if (a.start !== b.start) return a.start - b.start;
  if (a.displayEnd !== b.displayEnd) return b.displayEnd - a.displayEnd;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
};

const computeColumnLayout = (column: number, colSpan: number, totalColumns: number): EventDayLayout => {
  if (totalColumns <= 1) return { widthPercent: 100, leftPercent: 0 };
  const baseColumnWidth = 100 / totalColumns;
  const leftPercent = column * baseColumnWidth;
  const idealWidth = colSpan * baseColumnWidth + OVERLAP_PERCENT;
  const widthPercent = Math.min(idealWidth, 100 - leftPercent);
  return { widthPercent, leftPercent };
};

const flushCluster = (
  cluster: PlacedEvent[],
  columnEvents: PlacedEvent[][],
  totalColumns: number,
  result: Map<string, EventDayLayout>
) => {
  for (const placed of cluster) {
    let colSpan = 1;
    for (let nextCol = placed.column + 1; nextCol < totalColumns; nextCol++) {
      const eventsInCol = columnEvents[nextCol];
      const conflicts = eventsInCol?.some(
        (other) => other.start < placed.displayEnd && placed.start < other.displayEnd
      );
      if (conflicts) break;
      colSpan++;
    }
    result.set(placed.id, computeColumnLayout(placed.column, colSpan, totalColumns));
  }
};

export const getEventDayLayouts = <T extends OverlappingEventInput>(events: T[]): Map<string, EventDayLayout> => {
  const result = new Map<string, EventDayLayout>();
  if (events.length === 0) return result;

  const sorted: PreparedEvent[] = events
    .map((event) => {
      const duration = event.actualDuration ?? event.durationInMinutes;
      const displayDuration = Math.max(duration, MIN_DISPLAY_DURATION_MINUTES);
      return {
        id: event.id,
        start: event.startTimeInMS,
        displayEnd: addMinutes(event.startTimeInMS, displayDuration).getTime(),
      };
    })
    .sort(compareEvents);

  let cluster: PlacedEvent[] = [];
  let columnEvents: PlacedEvent[][] = [];
  let columnEndTimes: number[] = [];
  let clusterEnd = -Infinity;

  for (const event of sorted) {
    if (cluster.length > 0 && event.start >= clusterEnd) {
      flushCluster(cluster, columnEvents, columnEndTimes.length, result);
      cluster = [];
      columnEvents = [];
      columnEndTimes = [];
      clusterEnd = -Infinity;
    }

    let column = columnEndTimes.findIndex((endTime) => endTime <= event.start);
    if (column === -1) {
      column = columnEndTimes.length;
      columnEndTimes.push(event.displayEnd);
      columnEvents.push([]);
    } else {
      columnEndTimes[column] = event.displayEnd;
    }

    const placed: PlacedEvent = { ...event, column };
    cluster.push(placed);
    columnEvents[column]?.push(placed);
    if (event.displayEnd > clusterEnd) clusterEnd = event.displayEnd;
  }

  if (cluster.length > 0) flushCluster(cluster, columnEvents, columnEndTimes.length, result);

  return result;
};
